import { Activity, ExternalLink } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { formatNumber, formatUSD } from "../data/api";

const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const shortenWallet = (w) => (w ? `${w.slice(0, 4)}...${w.slice(-4)}` : "");

export const LiveTxFeed = ({ transactions }) => {
  const txs = transactions || [];

  return (
    <div data-testid="live-tx-feed" className="card-forge p-4 sm:p-6" style={{ borderRadius: "2px" }}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="bg-green-900/20 border border-green-900/50 p-2" style={{ borderRadius: "2px" }}>
            <Activity className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="font-cinzel text-base font-bold uppercase tracking-wider text-foreground">Live Feed</h2>
            <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">Real On-chain Trades</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="live-dot" />
          <span className="font-mono text-xs text-green-500">LIVE</span>
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 border-b border-forge-steel mb-1">
        <span className="col-span-1 font-mono text-[10px] text-muted-foreground uppercase">Type</span>
        <span className="col-span-2 font-mono text-[10px] text-muted-foreground uppercase">Amount</span>
        <span className="col-span-2 font-mono text-[10px] text-muted-foreground uppercase">Value</span>
        <span className="col-span-3 font-mono text-[10px] text-muted-foreground uppercase">Wallet</span>
        <span className="col-span-2 font-mono text-[10px] text-muted-foreground uppercase text-right">SOL</span>
        <span className="col-span-1 font-mono text-[10px] text-muted-foreground uppercase text-right">Time</span>
        <span className="col-span-1" />
      </div>

      {/* Mobile header */}
      <div className="grid md:hidden grid-cols-4 gap-2 px-2 py-2 border-b border-forge-steel mb-1">
        <span className="font-mono text-[10px] text-muted-foreground uppercase">Type</span>
        <span className="font-mono text-[10px] text-muted-foreground uppercase">Value</span>
        <span className="font-mono text-[10px] text-muted-foreground uppercase text-right">SOL</span>
        <span className="font-mono text-[10px] text-muted-foreground uppercase text-right">Time</span>
      </div>

      <ScrollArea className="h-[340px] feed-fade">
        <div className="space-y-0">
          {txs.map((tx, idx) => {
            const isBuy = tx.tx_type === "buy";
            return (
              <div key={tx.id || idx} data-testid={`tx-row-${idx}`}>
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2.5 items-center hover:bg-forge-raised/50 transition-colors duration-200 border-b border-forge-steel/15 last:border-0">
                  <span className={`col-span-1 font-mono text-[10px] font-bold uppercase ${isBuy ? "text-green-500" : "text-red-400"}`}>
                    {isBuy ? "BUY" : "SELL"}
                  </span>
                  <span className="col-span-2 font-mono text-sm text-foreground font-medium">
                    {formatNumber(tx.amount)}
                  </span>
                  <span className="col-span-2 font-mono text-xs text-muted-foreground">
                    {formatUSD(tx.usd_value)}
                  </span>
                  <span className="col-span-3 font-mono text-xs text-muted-foreground/70">
                    {shortenWallet(tx.wallet)}
                  </span>
                  <span className="col-span-2 font-mono text-xs text-fire-orange text-right">
                    {tx.sol_value >= 1 ? tx.sol_value.toFixed(2) : tx.sol_value.toFixed(4)} SOL
                  </span>
                  <span className="col-span-1 font-mono text-[10px] text-muted-foreground/50 text-right">
                    {formatTime(tx.timestamp)}
                  </span>
                  <a href={`https://solscan.io/tx/${tx.id}`} target="_blank" rel="noopener noreferrer" className="col-span-1 flex justify-end text-muted-foreground/30 hover:text-fire-red transition-colors duration-200" data-testid={`tx-link-${idx}`}>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Mobile row */}
                <a href={`https://solscan.io/tx/${tx.id}`} target="_blank" rel="noopener noreferrer" className="grid md:hidden grid-cols-4 gap-2 px-2 py-2.5 items-center hover:bg-forge-raised/50 transition-colors duration-200 border-b border-forge-steel/15 last:border-0 no-underline">
                  <span className={`font-mono text-[11px] font-bold uppercase ${isBuy ? "text-green-500" : "text-red-400"}`}>
                    {isBuy ? "BUY" : "SELL"}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {formatUSD(tx.usd_value)}
                  </span>
                  <span className="font-mono text-xs text-fire-orange text-right">
                    {tx.sol_value >= 1 ? tx.sol_value.toFixed(2) : tx.sol_value.toFixed(4)}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground/50 text-right">
                    {formatTime(tx.timestamp)}
                  </span>
                </a>
              </div>
            );
          })}
          {txs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Activity className="w-6 h-6 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground font-mono text-xs">Loading trade data...</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
